<template>
  <section
    id="problemsSection"
    class="bg-white py-20 tablet:py-28"
  >
    <div class="container">
      <div class="mx-auto max-w-3xl text-center">
        <i18n-t
          keypath="landing.problemSlider.title"
          scope="global"
          tag="h2"
          class="text-3xl font-bold tracking-tight tablet:text-[44px] tablet:leading-[1.1]"
        >
          <template #br><br /></template>
          <template #accent
            ><span class="text-gold-primary-120">{{ t('landing.problemSlider.titleAccent') }}</span></template
          >
        </i18n-t>
      </div>

      <div class="mt-14 grid gap-6 tablet:mt-[72px] tablet:grid-cols-3 [&>*]:min-w-0">
        <!-- Card 1: Pipeline -->
        <div
          ref="card1"
          class="problem-card"
        >
          <div class="flex flex-col gap-3">
            <div
              v-for="(s, i) in pipelineStages"
              :key="i"
              class="flex items-start gap-3"
            >
              <div class="flex shrink-0 flex-col items-center">
                <div
                  class="flex size-9 items-center justify-center rounded-full border-2"
                  :class="s.dotClass"
                >
                  <component
                    :is="s.icon"
                    class="size-4"
                  />
                </div>
                <div
                  v-if="s.line"
                  class="my-1 h-8 w-px"
                  :class="s.lineClass"
                ></div>
              </div>
              <div class="pb-1">
                <div
                  class="flex items-center gap-1 text-sm font-semibold"
                  :class="s.titleClass"
                >
                  <span>{{ s.title }}</span>
                  <span class="text-xs font-normal text-black/40">{{ s.meta }}</span>
                </div>
                <div
                  class="mt-0.5 text-xs"
                  :class="s.subClass"
                >
                  {{ s.sub }}
                </div>
              </div>
            </div>
          </div>
          <h3 class="mt-6 text-xl font-bold leading-[1.3] text-black-100 tablet:text-[22px]">
            {{ t('landing.problemSlider.card1Title') }}
          </h3>
          <p class="mt-2.5 text-sm leading-[1.5] text-black/60">{{ t('landing.problemSlider.card1Body') }}</p>
          <div class="mt-auto border-t border-black/[0.08] pt-6 text-start">
            <p class="text-[11px] font-semibold uppercase tracking-[0.15em] text-black/50">
              {{ t('landing.problemSlider.card1Eyebrow') }}
            </p>
            <p class="mt-2 text-sm text-black-80">{{ t('landing.problemSlider.card1Calc') }}</p>
            <p
              class="mt-3 text-[32px] font-extrabold leading-none tracking-[-0.03em] tabular-nums text-black-100 tablet:text-[38px]"
            >
              AED 176,000
            </p>
            <p class="mt-2 text-sm italic text-black/50">{{ t('landing.problemSlider.card1Footnote') }}</p>
          </div>
        </div>

        <!-- Card 2: Splits -->
        <div
          ref="card2"
          class="problem-card"
        >
          <div class="flex flex-col gap-3">
            <div
              v-for="(row, idx) in splitRows"
              :key="row.id"
              :class="row.dic ? 'mt-2 border-t border-dashed border-gold-primary-100/40 pt-4' : ''"
            >
              <div class="mb-1 flex items-center justify-between text-xs">
                <span :class="row.dic ? 'font-semibold text-gold-primary-120' : 'text-black/60'">{{ row.label }}</span>
                <span
                  class="font-semibold"
                  :class="row.dic ? 'text-gold-primary-120' : 'text-black-100'"
                  >{{ row.pct }}</span
                >
              </div>
              <div
                class="h-3 overflow-hidden rounded-full"
                :class="row.dic ? 'bg-gold-primary-10' : 'bg-black/5'"
              >
                <div
                  class="h-full rounded-full transition-[width] duration-[900ms] ease-out"
                  :class="
                    row.dic
                      ? 'bg-gradient-to-r from-gold-primary-100 to-gold-primary-120'
                      : idx === 0
                        ? 'bg-black/40'
                        : 'bg-black/50'
                  "
                  :style="{ width: row.width }"
                ></div>
              </div>
            </div>
          </div>
          <p class="mt-3 text-center text-[11px] italic">
            <span class="text-black/30 line-through">{{ t('landing.problemSlider.ceiling') }}</span>
            <span class="ms-1.5 text-gold-primary-120">{{ t('landing.problemSlider.untilItDoes') }}</span>
          </p>
          <h3 class="mt-5 text-xl font-bold leading-[1.3] text-black-100 tablet:text-[22px]">
            {{ t('landing.problemSlider.card2Title') }}
          </h3>
        </div>

        <!-- Card 3: Chat -->
        <div
          ref="card3"
          class="problem-card"
        >
          <div class="relative overflow-hidden rounded-2xl border border-black/[0.06] bg-white">
            <div
              v-for="(c, i) in chatRows"
              :key="i"
              class="flex items-center gap-2.5 border-t border-black/[0.05] px-3 py-2.5 first:border-t-0"
              :class="{ 'opacity-70': c.faded }"
            >
              <div
                class="flex size-10 shrink-0 items-center justify-center rounded-full text-xs font-semibold text-white"
                :style="{ background: `linear-gradient(135deg, ${c.a}, ${c.b})` }"
              >
                {{ c.initials }}
              </div>
              <div class="min-w-0 flex-1">
                <div class="flex justify-between gap-2">
                  <span class="truncate text-sm font-semibold text-black-100">{{ c.name }}</span>
                  <span class="shrink-0 text-[10px] text-black/40">{{ c.time }}</span>
                </div>
                <p class="truncate text-xs text-black/55">{{ c.preview }}</p>
              </div>
              <span
                class="inline-flex h-5 min-w-[22px] items-center justify-center rounded-full bg-emerald-500 px-1.5 text-[10px] font-semibold text-white"
                >{{ c.unread }}</span
              >
            </div>
            <div
              class="pointer-events-none absolute inset-x-0 bottom-0 h-10 bg-gradient-to-b from-transparent to-white"
            ></div>
          </div>
          <p class="mt-3 text-center text-[11px] italic text-black/50">{{ t('landing.problemSlider.chatFootnote') }}</p>
          <h3 class="mt-5 text-xl font-bold leading-[1.3] text-black-100 tablet:text-[22px]">
            {{ t('landing.problemSlider.card3Title') }}
          </h3>
          <p class="mt-2.5 text-sm leading-[1.5] text-black/60">{{ t('landing.problemSlider.card3Body') }}</p>
          <div class="mt-auto border-t border-black/[0.08] pt-6 text-start">
            <p class="text-[11px] font-semibold uppercase tracking-[0.15em] text-black/50">
              {{ t('landing.problemSlider.card3Eyebrow') }}
            </p>
            <i18n-t
              keypath="landing.problemSlider.card3Calc"
              scope="global"
              tag="p"
              class="mt-2 text-sm text-black-80"
            >
              <template #hours
                ><b class="font-semibold text-black-100">{{ t('landing.problemSlider.card3Hours') }}</b></template
              >
            </i18n-t>
            <p
              class="mt-3 text-[32px] font-extrabold leading-none tracking-[-0.03em] tabular-nums text-black-100 tablet:text-[38px]"
            >
              {{ t('landing.problemSlider.card3Amount') }}
            </p>
            <p class="mt-2 text-sm italic text-black/50">{{ t('landing.problemSlider.card3Footnote') }}</p>
          </div>
        </div>
      </div>

      <i18n-t
        keypath="landing.problemSlider.closing"
        scope="global"
        tag="p"
        class="mx-auto mt-16 max-w-3xl text-center text-xl font-semibold leading-snug text-black-100 tablet:text-2xl"
      >
        <template #accent
          ><span class="text-gold-primary-120">{{ t('landing.problemSlider.closingAccent') }}</span></template
        >
      </i18n-t>
      <i18n-t
        keypath="landing.problemSlider.closingSub"
        scope="global"
        tag="p"
        class="mt-3 text-center text-base text-black-80"
      >
        <template #accent
          ><span class="font-semibold text-black-100">{{ t('landing.problemSlider.closingSubAccent') }}</span></template
        >
      </i18n-t>
    </div>
  </section>
</template>

<script setup lang="ts">
import { MARKET_TOP_AGENT_SPLIT_PERCENT, MARKET_TYPICAL_SPLIT_PERCENT } from '~/constants/landing';
import { useIntersectionObserver } from '@vueuse/core';
import { h } from 'vue';

// Inline icon components to avoid dep churn
const CheckIcon = () =>
  h('svg', { viewBox: '0 0 20 20', fill: 'currentColor' }, [
    h('path', { d: 'M16.7 5.3a1 1 0 010 1.4l-8 8a1 1 0 01-1.4 0l-4-4a1 1 0 011.4-1.4L8 12.6l7.3-7.3a1 1 0 011.4 0z' }),
  ]);
const ClockIcon = () =>
  h('svg', { viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', 'stroke-width': 2 }, [
    h('circle', { cx: 12, cy: 12, r: 10 }),
    h('path', { d: 'M12 6v6l4 2', 'stroke-linecap': 'round' }),
  ]);

const { t } = useI18n();

const pipelineStages = computed(() => [
  {
    icon: CheckIcon,
    title: t('landing.problemSlider.stageSpaTitle'),
    meta: t('landing.problemSlider.stageSpaMeta'),
    sub: t('landing.problemSlider.stageSpaSub'),
    dotClass: 'border-emerald-500 bg-emerald-100/50 text-emerald-600',
    titleClass: 'text-black-100',
    subClass: 'text-emerald-600 font-medium',
    line: true,
    lineClass: 'bg-gradient-to-b from-emerald-500 to-black/10',
  },
  {
    icon: ClockIcon,
    title: t('landing.problemSlider.stageWaitingTitle'),
    meta: t('landing.problemSlider.stageWaitingMeta'),
    sub: t('landing.problemSlider.stageWaitingSub'),
    dotClass: 'border-dashed border-black/20 bg-black/[0.04] text-black-60',
    titleClass: 'text-black-60',
    subClass: 'text-black-60',
    line: true,
    lineClass: 'bg-gradient-to-b from-black/10 to-red-400',
  },
  {
    icon: ClockIcon,
    title: t('landing.problemSlider.stagePaidTitle'),
    meta: t('landing.problemSlider.stagePaidMeta'),
    sub: t('landing.problemSlider.stagePaidSub'),
    dotClass: 'border-red-400 bg-red-100/60 text-red-500',
    titleClass: 'text-black-100',
    subClass: 'text-red-500 font-medium',
    line: false,
  },
]);

const { topSelfClosePercent } = useLandingFacts();

const splitRow = (id: string, label: string, percent: number, dic = false) => ({
  id,
  label,
  pct: `${percent}%`,
  width: `${percent}%`,
  dic,
});

// The Insiders bar follows the tier config; it is left out until the config has loaded.
const splitRows = computed(() => [
  splitRow('typical', t('landing.problemSlider.splitTypical'), MARKET_TYPICAL_SPLIT_PERCENT),
  splitRow('topAgent', t('landing.problemSlider.splitTopAgent'), MARKET_TOP_AGENT_SPLIT_PERCENT),
  ...(topSelfClosePercent.value === null
    ? []
    : [
        splitRow(
          'insiders',
          t('landing.problemSlider.splitInsiders', { percent: topSelfClosePercent.value }),
          topSelfClosePercent.value,
          true,
        ),
      ]),
]);

const chatRows = computed(() => [
  {
    initials: 'DA',
    name: t('landing.problemSlider.chat1Name'),
    time: '14:22',
    preview: t('landing.problemSlider.chat1Preview'),
    unread: '43',
    a: '#10B981',
    b: '#059669',
  },
  {
    initials: 'EM',
    name: t('landing.problemSlider.chat2Name'),
    time: '14:18',
    preview: t('landing.problemSlider.chat2Preview'),
    unread: '128',
    a: '#3B82F6',
    b: '#1D4ED8',
  },
  {
    initials: 'SO',
    name: t('landing.problemSlider.chat3Name'),
    time: '13:55',
    preview: t('landing.problemSlider.chat3Preview'),
    unread: '27',
    a: '#A855F7',
    b: '#6B21A8',
  },
  {
    initials: 'OP',
    name: t('landing.problemSlider.chat4Name'),
    time: '13:40',
    preview: t('landing.problemSlider.chat4Preview'),
    unread: '99+',
    a: '#F97316',
    b: '#DC2626',
  },
  {
    initials: 'AR',
    name: t('landing.problemSlider.chat5Name'),
    time: '13:22',
    preview: t('landing.problemSlider.chat5Preview'),
    unread: '56',
    a: '#F59E0B',
    b: '#B45309',
    faded: true,
  },
]);

const card1 = ref<HTMLElement | null>(null);
const card2 = ref<HTMLElement | null>(null);
const card3 = ref<HTMLElement | null>(null);
[card1, card2, card3].forEach((r) => {
  useIntersectionObserver(
    r,
    ([e]) => {
      if (e.isIntersecting) (e.target as HTMLElement).classList.add('revealed');
    },
    { threshold: 0.25 },
  );
});
</script>

<style scoped lang="scss">
.problem-card {
  @apply flex flex-col rounded-[20px] border border-black/[0.08] bg-gold-primary-10 p-6 opacity-0 tablet:p-8;
  transform: translateY(12px);
  transition:
    opacity 500ms ease,
    transform 500ms cubic-bezier(0.25, 1, 0.35, 1);
}
.problem-card:hover {
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.06);
}
.problem-card.revealed {
  @apply opacity-100;
  transform: translateY(0);
}
</style>
